import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Observable, catchError, forkJoin, map, of, startWith, switchMap } from 'rxjs';
import {
  SurveySectionQuestionEditComponent,
  surveyQuestionTextMinCharactersValidators,
} from 'src/app/components/main/employees/rrhh/survey-edit/survey-section-edit/survey-section-question-edit/survey-section-question-edit.component';
import {
  TargetedCompanyProductsCardComponent,
  TargetedCompanyProductOption,
  getToAskAboutCompanyProductScope,
  getToAskAboutCompanyProducts,
  setTargetedCompanyProductSelection,
} from 'src/app/components/main/shared/targeted-company-products-card/targeted-company-products-card.component';
import {
  TargetedDepartmentCategoriesCardComponent,
  TargetedDepartmentCategoryOption,
  getToAnswerDepartmentCategoryIds,
  setTargetedDepartmentCategorySelection,
} from 'src/app/components/main/shared/targeted-department-categories-card/targeted-department-categories-card.component';
import {
  TargetedRolesCardComponent,
  getToAnswerRoleIds,
  setTargetedRoleSelection,
} from 'src/app/components/main/shared/targeted-roles-card/targeted-roles-card.component';
import {
  TargetedUsersCardComponent,
  TargetedUserOption,
  getToAskAboutUsers,
  getToAskAboutUsersScope,
  setTargetedUserSelection,
} from 'src/app/components/main/shared/targeted-users-card/targeted-users-card.component';
import { RoleSlugEnum } from 'src/app/enums/role-slug.enum';
import { SurveyVariableConditionFilterCompanyProductScopeTypeEnum } from 'src/app/enums/survey-variable-condition-filter-company-product-scope-type.enum';
import { SurveyVariableConditionFilterDirectionEnum } from 'src/app/enums/survey-variable-condition-filter-direction.enum';
import { SurveyVariableConditionFilterUsersScopeTypeEnum } from 'src/app/enums/survey-variable-condition-filter-users-scope-type.enum';
import { SurveyQuestionTypeEnum, hasEditableResponseOptions } from 'src/app/enums/survey-question-type.enum';
import { SurveyVariableConditionSectionQuestionAnonymousScopeEnum } from 'src/app/enums/survey-variable-condition-section-question-anonymous-scope.enum';
import { ComponentCanDeactivate, PendingChangesGuard } from 'src/app/guards/pending-changes.guard';
import { DepartmentCategory } from 'src/app/models/department_category.model';
import { Role } from 'src/app/models/role.model';
import { SurveyQuestionItem } from 'src/app/models/survey-question-item.model';
import { Survey } from 'src/app/models/survey.model';
import {
  SurveySectionQuestionForm,
  SurveySectionQuestionItemForm,
  SurveyVariableConditionSection,
} from 'src/app/models/survey-variable-condition-section.model';
import { ApiService } from 'src/app/services';
import { getEditableSurveyVariableCondition } from '../survey-edit.component';

@Component({
  selector: 'app-survey-section-edit',
  templateUrl: './survey-section-edit.component.html',
  styleUrl: './survey-section-edit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    DragDropModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    RouterLink,
    SurveySectionQuestionEditComponent,
    TargetedRolesCardComponent,
    TargetedDepartmentCategoriesCardComponent,
    TargetedCompanyProductsCardComponent,
    TargetedUsersCardComponent,
  ],
})
export class SurveySectionEditComponent implements ComponentCanDeactivate {
  private readonly api_service = inject(ApiService);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly activated_route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly form_builder = inject(FormBuilder);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly change_detector = inject(ChangeDetectorRef);
  private readonly pending_changes_guard = inject(PendingChangesGuard);

  readonly loading = signal(false);
  readonly roles = signal<Role[]>([]);
  readonly survey_title = signal<string | null>(null);
  readonly section = signal<SurveyVariableConditionSection | null>(null);
  readonly config_expanded = signal(true);
  readonly scope_expanded = signal(true);
  private next_question_form_key = 1;
  readonly roles_error = signal<string | null>(null);
  readonly department_categories_error = signal<string | null>(null);
  readonly company_products_error = signal<string | null>(null);
  readonly users_error = signal<string | null>(null);

  readonly survey_id = toSignal(
    this.activated_route.paramMap.pipe(
      map(param_map => {
        const raw_id = param_map.get('id');
        const parsed_id = raw_id ? Number.parseInt(raw_id, 10) : Number.NaN;

        return Number.isNaN(parsed_id) ? null : parsed_id;
      })
    ),
    { initialValue: null as number | null }
  );

  readonly section_id = toSignal(
    this.activated_route.paramMap.pipe(
      map(param_map => {
        const raw_id = param_map.get('section_id');
        const parsed_id = raw_id ? Number.parseInt(raw_id, 10) : Number.NaN;

        return Number.isNaN(parsed_id) ? null : parsed_id;
      })
    ),
    { initialValue: null as number | null }
  );

  private readonly route_ids = computed(() => ({
    survey_id: this.survey_id(),
    section_id: this.section_id(),
  }));

  private readonly page_load = toSignal(
    toObservable(this.route_ids).pipe(
      switchMap(({ survey_id, section_id }): Observable<SectionPageLoad> => {
        if (survey_id === null || section_id === null) {
          return of({ status: 'redirect_surveys' });
        }

        return forkJoin({
          roles: this.api_service.getRoles(),
          survey: this.api_service.getSurvey(survey_id),
        }).pipe(
          map(({ roles, survey }): SectionPageLoad => {
            const active_condition = getEditableSurveyVariableCondition(survey);
            const section = active_condition?.survey_variable_condition_sections?.find(item => item.id === section_id) ?? null;

            if (section === null) {
              return { status: 'redirect_edit', survey_id };
            }

            return { status: 'ready', roles, survey, section };
          }),
          catchError(() => of<SectionPageLoad>({ status: 'redirect_edit', survey_id }))
        );
      })
    ),
    { initialValue: { status: 'loading' } as SectionPageLoad }
  );

  readonly loading_data = computed(() => this.page_load().status === 'loading');

  readonly form = this.form_builder.nonNullable.group({
    title: ['', [Validators.required, Validators.maxLength(255)]],
    role_ids: [[] as number[], [minSelectedValidator(1)]],
    department_category_ids: [[] as number[]],
    company_product_scope_type: [SurveyVariableConditionFilterCompanyProductScopeTypeEnum.None],
    company_products: [[] as TargetedCompanyProductOption[]],
    users_scope_type: [SurveyVariableConditionFilterUsersScopeTypeEnum.None],
    users: [[] as TargetedUserOption[]],
    questions: this.form_builder.array<SurveySectionQuestionForm>([]),
  });

  private readonly selected_role_ids = toSignal(
    this.form.controls.role_ids.valueChanges.pipe(startWith(this.form.controls.role_ids.value)),
    { initialValue: this.form.controls.role_ids.value }
  );

  readonly department_categories = toSignal(
    this.api_service.getDepartmentCategories({ per_page: 500, order_by: 'name:asc' }).pipe(
      map((response): DepartmentCategory[] => (Array.isArray(response) ? response : (response.data ?? []))),
      catchError(() => of<DepartmentCategory[]>([]))
    ),
    { initialValue: [] as DepartmentCategory[] }
  );

  readonly worker_role_selected = computed((): boolean =>
    this.roles().some(role => role.slug === RoleSlugEnum.WORKER && this.selected_role_ids().includes(role.id))
  );

  readonly department_category_options = computed((): TargetedDepartmentCategoryOption[] =>
    this.department_categories().map(department_category => ({
      id: department_category.id,
      name: department_category.name,
    }))
  );

  readonly title_draft = toSignal(this.form.controls.title.valueChanges.pipe(startWith(this.form.controls.title.value)), {
    initialValue: '',
  });

  readonly breadcrumb_survey_title = computed(() => this.survey_title());

  readonly breadcrumb_section_title = computed(() => {
    const draft = this.title_draft().trim();

    if (draft !== '') {
      return draft;
    }

    return this.section()?.title ?? null;
  });

  readonly survey_edit_link = computed(() => {
    const survey_id = this.survey_id();

    return survey_id === null ? ['/employees', 'rrhh', 'surveys'] : ['/employees', 'rrhh', 'surveys', survey_id, 'edit'];
  });

  constructor() {
    this.pending_changes_guard.registerBeforeUnload(this, this.destroy_ref);

    toObservable(this.page_load)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(load => {
        if (load.status === 'loading') {
          return;
        }

        if (load.status === 'redirect_surveys') {
          void this.router.navigate(['/employees', 'rrhh', 'surveys']);

          return;
        }

        if (load.status === 'redirect_edit') {
          void this.router.navigate(['/employees', 'rrhh', 'surveys', load.survey_id, 'edit']);

          return;
        }

        this.roles.set(load.roles);
        this.survey_title.set(load.survey.title);
        this.section.set(load.section);
        this.patchSectionForm(load.section, load.survey);
      });
  }

  canDeactivate(): boolean {
    return !this.form.dirty;
  }

  onRoleIdsChange(role_ids: number[]): void {
    setTargetedRoleSelection(this.form.controls.role_ids, role_ids);
    this.refreshRolesError();

    if (!this.includesWorkerRole(role_ids)) {
      setTargetedDepartmentCategorySelection(this.form.controls.department_category_ids, []);
    }

    this.syncWorkerDepartmentCategoriesValidator();
  }

  onDepartmentCategoryIdsChange(department_category_ids: number[]): void {
    setTargetedDepartmentCategorySelection(this.form.controls.department_category_ids, department_category_ids);
    this.refreshDepartmentCategoriesError();
  }

  onCompanyProductScopeChange(scope: SurveyVariableConditionFilterCompanyProductScopeTypeEnum): void {
    setTargetedCompanyProductSelection(
      this.form.controls.company_product_scope_type,
      this.form.controls.company_products,
      scope,
      scope === SurveyVariableConditionFilterCompanyProductScopeTypeEnum.Manual ? this.form.controls.company_products.value : []
    );
    this.form.controls.company_products.markAsUntouched();
    this.syncManualCompanyProductsValidator();
  }

  onCompanyProductsChange(products: TargetedCompanyProductOption[]): void {
    setTargetedCompanyProductSelection(
      this.form.controls.company_product_scope_type,
      this.form.controls.company_products,
      this.form.controls.company_product_scope_type.value,
      products
    );
    this.form.controls.company_products.markAsTouched();
    this.syncManualCompanyProductsValidator();
  }

  onCompanyProductsBlur(): void {
    this.form.controls.company_products.markAsTouched();
    this.refreshCompanyProductsError();
  }

  onUsersScopeChange(scope: SurveyVariableConditionFilterUsersScopeTypeEnum): void {
    setTargetedUserSelection(
      this.form.controls.users_scope_type,
      this.form.controls.users,
      scope,
      scope === SurveyVariableConditionFilterUsersScopeTypeEnum.Manual ? this.form.controls.users.value : []
    );
    this.form.controls.users.markAsUntouched();
    this.syncManualUsersValidator();
  }

  onUsersChange(users: TargetedUserOption[]): void {
    setTargetedUserSelection(
      this.form.controls.users_scope_type,
      this.form.controls.users,
      this.form.controls.users_scope_type.value,
      users
    );
    this.form.controls.users.markAsTouched();
    this.syncManualUsersValidator();
  }

  onUsersBlur(): void {
    this.form.controls.users.markAsTouched();
    this.refreshUsersError();
  }

  save(): void {
    this.syncWorkerDepartmentCategoriesValidator();
    this.syncManualCompanyProductsValidator();
    this.syncManualUsersValidator();
    this.form.markAllAsTouched();
    this.refreshRolesError();
    this.refreshDepartmentCategoriesError();
    this.refreshCompanyProductsError();
    this.refreshUsersError();

    if (this.loading() || this.loading_data()) {
      return;
    }

    if (this.form.invalid) {
      this.snack_bar.open('Completa los campos obligatorios para guardar.', undefined, { duration: 4000 });

      return;
    }

    const survey_id = this.survey_id();
    const section_id = this.section_id();

    if (survey_id === null || section_id === null) {
      return;
    }

    const values = this.form.getRawValue();
    const title = values.title.trim();
    const company_product_scope_type = values.company_product_scope_type;
    const company_product_ids =
      company_product_scope_type === SurveyVariableConditionFilterCompanyProductScopeTypeEnum.Manual
        ? values.company_products.map(product => product.id)
        : [];
    const users_scope_type = values.users_scope_type;
    const user_ids = users_scope_type === SurveyVariableConditionFilterUsersScopeTypeEnum.Manual ? values.users.map(user => user.id) : [];
    const questions = values.questions.map(question => ({
      id: question.id,
      statement: question.statement.trim(),
      type: question.type,
      min_characters: question.type === SurveyQuestionTypeEnum.Text ? question.min_characters : null,
      is_required: question.is_required,
      allows_dont_know: question.allows_dont_know,
      anonymous_scope: question.anonymous_scope,
      items: hasEditableResponseOptions(question.type)
        ? question.items.map((item, index) => ({
            id: item.id,
            statement: item.statement.trim(),
            description: item.description?.trim() ? item.description.trim() : null,
            order: index,
          }))
        : [],
    }));

    this.loading.set(true);

    this.api_service
      .updateSurveyVariableConditionSection(section_id, {
        title,
        questions,
        filter: {
          role_ids: values.role_ids,
          department_category_ids: this.worker_role_selected() ? values.department_category_ids : [],
          company_product_scope_type,
          company_product_ids,
          users_scope_type,
          user_ids,
        },
      })
      .subscribe({
        next: (section: SurveyVariableConditionSection) => {
          this.section.set(section);
          this.patchSectionForm(section);
          this.loading.set(false);

          if (section.id !== section_id) {
            void this.router.navigate(['/employees', 'rrhh', 'surveys', survey_id, 'edit', 'sections', section.id, 'edit'], {
              replaceUrl: true,
            });
          }
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  addQuestion(): void {
    if (this.loading() || this.loading_data()) {
      return;
    }

    this.form.controls.questions.push(this.createQuestionForm());
    this.form.markAsDirty();
    this.change_detector.markForCheck();
  }

  dropQuestion(event: CdkDragDrop<SurveySectionQuestionForm[]>): void {
    if (this.loading() || this.loading_data() || event.previousIndex === event.currentIndex) {
      return;
    }

    const questions = this.form.controls.questions;
    const moved_question = questions.at(event.previousIndex);
    questions.removeAt(event.previousIndex, { emitEvent: false });
    questions.insert(event.currentIndex, moved_question, { emitEvent: false });
    this.form.markAsDirty();
    this.change_detector.markForCheck();
  }

  removeQuestion(question_index: number): void {
    if (this.loading() || this.loading_data()) {
      return;
    }

    const question_form = this.form.controls.questions.at(question_index);
    const statement = question_form.controls.statement.value.trim();
    const question_label = statement !== '' ? statement : `Pregunta ${question_index + 1}`;

    if (!confirm(`¿Eliminar la pregunta "${question_label}"?`)) {
      return;
    }

    this.form.controls.questions.removeAt(question_index);
    this.form.markAsDirty();
    this.change_detector.markForCheck();
  }

  private patchSectionForm(section: SurveyVariableConditionSection, survey: Survey | null = null): void {
    const filters = section.survey_variable_condition_filters ?? [];
    const has_section_audience_filter = filters.some(filter => filter.direction === SurveyVariableConditionFilterDirectionEnum.ToAnswer);
    const survey_filters = survey !== null ? (getEditableSurveyVariableCondition(survey)?.survey_variable_condition_filters ?? []) : [];
    const audience_filters = has_section_audience_filter ? filters : survey_filters;

    this.form.patchValue({
      title: section.title,
      role_ids: getToAnswerRoleIds(audience_filters, this.roles()),
      department_category_ids: getToAnswerDepartmentCategoryIds(audience_filters),
      company_product_scope_type: getToAskAboutCompanyProductScope(filters),
      company_products: getToAskAboutCompanyProducts(filters),
      users_scope_type: getToAskAboutUsersScope(filters),
      users: getToAskAboutUsers(filters),
    });
    this.form.controls.questions.clear();

    const questions = [...(section.survey_variable_condition_section_questions ?? [])].sort((left, right) => left.order - right.order);

    for (const question of questions) {
      this.form.controls.questions.push(this.createQuestionForm(question));
    }

    this.syncWorkerDepartmentCategoriesValidator();
    this.syncManualCompanyProductsValidator();
    this.syncManualUsersValidator();
    this.refreshRolesError();
    this.form.markAsPristine();
  }

  private createQuestionForm(
    question?: NonNullable<SurveyVariableConditionSection['survey_variable_condition_section_questions']>[number]
  ): SurveySectionQuestionForm {
    const type = question?.survey_question.type ?? SurveyQuestionTypeEnum.Text;
    const items = hasEditableResponseOptions(type)
      ? [...(question?.survey_question.survey_question_items ?? [])].sort((left, right) => left.order - right.order)
      : [];

    return this.form_builder.group({
      id: this.form_builder.control<number | null>(question?.id ?? null),
      form_key: this.form_builder.nonNullable.control(this.next_question_form_key++),
      expanded: this.form_builder.nonNullable.control<boolean>(true),
      is_required: this.form_builder.nonNullable.control(question?.is_required ?? false),
      allows_dont_know: this.form_builder.nonNullable.control(question?.allows_dont_know ?? false),
      anonymous_scope: this.form_builder.nonNullable.control(
        question?.anonymous_scope ?? SurveyVariableConditionSectionQuestionAnonymousScopeEnum.FullAnonymous,
        Validators.required
      ),
      type: this.form_builder.nonNullable.control(type, Validators.required),
      min_characters: this.form_builder.control<number | null>(
        type === SurveyQuestionTypeEnum.Text ? (question?.survey_question.min_characters ?? 255) : null,
        type === SurveyQuestionTypeEnum.Text ? surveyQuestionTextMinCharactersValidators() : []
      ),
      statement: this.form_builder.nonNullable.control(question?.survey_question.statement ?? '', [
        Validators.required,
        Validators.maxLength(255),
      ]),
      items: this.form_builder.array(items.map((item, index) => this.createQuestionItemForm(item, index))),
    });
  }

  private createQuestionItemForm(item?: SurveyQuestionItem, order = 0): SurveySectionQuestionItemForm {
    return this.form_builder.group({
      id: this.form_builder.control<number | null>(item?.id ?? null),
      statement: this.form_builder.nonNullable.control(item?.statement ?? '', [Validators.required, Validators.maxLength(255)]),
      description: this.form_builder.control<string | null>(item?.description ?? null),
      order: this.form_builder.nonNullable.control(item?.order ?? order),
    });
  }

  private syncWorkerDepartmentCategoriesValidator(): void {
    const categories_control = this.form.controls.department_category_ids;

    categories_control.setValidators(this.includesWorkerRole() ? [minSelectedValidator(1)] : []);
    categories_control.updateValueAndValidity({ emitEvent: false });
    this.refreshDepartmentCategoriesError();
  }

  private syncManualCompanyProductsValidator(): void {
    const products_control = this.form.controls.company_products;
    const is_manual =
      this.form.controls.company_product_scope_type.value === SurveyVariableConditionFilterCompanyProductScopeTypeEnum.Manual;

    products_control.setValidators(is_manual ? [minSelectedValidator(1)] : []);
    products_control.updateValueAndValidity({ emitEvent: false });
    this.refreshCompanyProductsError();
  }

  private syncManualUsersValidator(): void {
    const users_control = this.form.controls.users;
    const is_manual = this.form.controls.users_scope_type.value === SurveyVariableConditionFilterUsersScopeTypeEnum.Manual;

    users_control.setValidators(is_manual ? [minSelectedValidator(1)] : []);
    users_control.updateValueAndValidity({ emitEvent: false });
    this.refreshUsersError();
  }

  private refreshRolesError(): void {
    const roles_control = this.form.controls.role_ids;

    this.roles_error.set(roles_control.touched && roles_control.hasError('minSelected') ? 'Selecciona al menos un rol.' : null);
  }

  private refreshDepartmentCategoriesError(): void {
    const categories_control = this.form.controls.department_category_ids;

    this.department_categories_error.set(
      categories_control.touched && categories_control.hasError('minSelected') ? 'Selecciona al menos una categoría de departamento.' : null
    );
  }

  private refreshCompanyProductsError(): void {
    const products_control = this.form.controls.company_products;

    this.company_products_error.set(
      products_control.touched && products_control.hasError('minSelected')
        ? 'Selecciona al menos un producto en la selección manual.'
        : null
    );
  }

  private refreshUsersError(): void {
    const users_control = this.form.controls.users;

    this.users_error.set(
      users_control.touched && users_control.hasError('minSelected') ? 'Selecciona al menos un usuario en la selección manual.' : null
    );
  }

  private includesWorkerRole(role_ids: number[] = this.form.controls.role_ids.value): boolean {
    return this.roles().some(role => role.slug === RoleSlugEnum.WORKER && role_ids.includes(role.id));
  }
}

function minSelectedValidator(min: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!Array.isArray(value) || value.length < min) {
      return { minSelected: { min, actual: Array.isArray(value) ? value.length : 0 } };
    }

    return null;
  };
}

type SectionPageLoad =
  | { status: 'loading' }
  | { status: 'redirect_surveys' }
  | { status: 'redirect_edit'; survey_id: number }
  | { status: 'ready'; roles: Role[]; survey: Survey; section: SurveyVariableConditionSection };
